import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ExternalLink, AlertTriangle } from "lucide-react";
import type { PageIssue, MixedContentDetails } from "@/types/technicalAudit";

interface MixedContentCardProps {
	issue: PageIssue;
}

export function MixedContentCard({ issue }: MixedContentCardProps) {
	const details = issue.issue_details as MixedContentDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Mixed Content</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This HTTPS page loads an HTTP resource. Browsers may block mixed content, causing broken functionality or security warnings.
					</p>
				</div>
				<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
					{details.resource_type}
				</Badge>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-950/40 flex items-center justify-center">
							<ShieldAlert className="w-4 h-4 text-green-600 dark:text-green-400" />
						</div>
						HTTPS Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={details.page_url}
						onClick={(e) => openWithCacheBuster(e, details.page_url)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-green-50 dark:bg-green-950/40/50 border-green-200 dark:border-green-800 hover:border-green-300 transition-all"
					>
						<p className="text-sm font-mono break-all text-green-700 dark:text-green-400 flex-1">{details.page_url}</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Insecure HTTP Resource
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="p-3 rounded-lg border bg-red-50 dark:bg-red-950/40/50 border-red-200 dark:border-red-800">
						<p className="text-sm font-mono break-all text-red-700 dark:text-red-400">{details.insecure_url}</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
